import { Button, Carousel, EButtonColorVariant } from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { goToRentals } from '@/actions/rentals';
import { ListingTile } from '@/components/ui/ListingTile';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { getRentalsTiles } from '@/redux/selectors/listing/similarRentals';
import {
  trackListingImageViewedEvent,
  trackListingSelectedEvent,
} from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { trackEvent } from '@/services/track-event';
import { IRentalTile } from '@/utility/mapSearchResultToTile';

type TNearbyRVsProps = {
  address?: string;
};

export const NearbyRVs = ({ address }: TNearbyRVsProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { isMobile, isAboveDesktop } = useBreakpoint();

  const superhostBadgeEnabled = useExperimentIsEnabled(OptimizelyFlags.SUPERHOST_BADGE);

  const rentals = useSelector(getRentalsTiles);

  if (!rentals?.length) return null;

  const onClickRental = (rental: IRentalTile, index: number) => {
    if (!rental?.id) return;

    trackListingSelectedEvent({
      rental,
      eventSource: EListingSource.CAMPGROUND_CAROUSEL,
      listingPlacement: index + 1,
    });
  };

  const onSlideChange = (rental: IRentalTile, index: number, nextIndex: number) => {
    if (!rental?.id) return;

    trackListingImageViewedEvent(rental, EListingSource.CAMPGROUND_CAROUSEL, index + 1, nextIndex);
  };

  const onSearchAllNearbyClick = () => {
    trackEvent({
      event: 'Growth : Rental listing : Search more',
      action: 'click',
    });

    dispatch(goToRentals(address));
  };

  return (
    <div>
      <Carousel
        content={rentals.map((rental, index) => (
          <ListingTile
            key={rental.id}
            rentalTile={rental}
            // Carousel inside carousel is not supported for touch devices.
            // To avoid issues, just show the first image for mobiles.
            showFirstImageOnly={isMobile}
            onSlideChange={nextIndex => onSlideChange(rental, index, nextIndex)}
            target={isAboveDesktop ? '_blank' : undefined}
            onClick={() => onClickRental(rental, index)}
            showSuperhost={superhostBadgeEnabled}
            wishlistingEventData={{
              index,
              eventSource: EListingSource.CAMPGROUND_CAROUSEL,
            }}
            dataTestId="listing-tile"
          />
        ))}
        slidesPerGroup={1}
        slidesPerView={isMobile ? 1.2 : isAboveDesktop ? 4 : 3}
        autoHideNavigationArrows
        title={intl.formatMessage({
          defaultMessage: 'Get there in a sweet RV',
          id: 'mywNP1',
        })}
      />

      <Button
        onClick={onSearchAllNearbyClick}
        variant={EButtonColorVariant.Primary}
        label={intl.formatMessage({
          defaultMessage: 'Search all nearby RVs',
          id: 'H0G73r',
        })}
      />
    </div>
  );
};
